import {
  inferBuilderSlotFromKey,
  resolveSectionDefinitions,
  resolveSectionKeys,
} from './aiPromptConfigUtils';

export const DEFAULT_SECTION_KEYS = [
  'scope',
  'technical',
  'eligibility',
  'commercial',
];

export const humanizeSectionKey = (key = '') =>
  String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

export const resolveSectionOrder = (generatedContent, promptConfig) => {
  const fromDefs = resolveSectionKeys(promptConfig);
  if (fromDefs.length) return fromDefs;

  const fromContent = generatedContent && typeof generatedContent === 'object'
    ? Object.keys(generatedContent).filter(
        (key) =>
          generatedContent[key] &&
          typeof generatedContent[key] === 'object' &&
          (generatedContent[key].title || (generatedContent[key].points || []).length),
      )
    : [];

  if (fromContent.length) return fromContent;

  return [...DEFAULT_SECTION_KEYS];
};

export const getSectionEntries = (generatedContent, promptConfig) => {
  const definitions = resolveSectionDefinitions(promptConfig);
  const defByKey = Object.fromEntries(definitions.map((d) => [d.key, d]));
  const order = resolveSectionOrder(generatedContent, promptConfig);

  return order
    .map((key) => {
      const section = generatedContent?.[key];
      if (!section || typeof section !== 'object') return null;
      const points = Array.isArray(section.points)
        ? section.points.map((p) => String(p)).filter(Boolean)
        : [];
      const def = defByKey[key];
      return {
        key,
        title: String(
          section.title || def?.label || humanizeSectionKey(key),
        ),
        points,
        builderSlot: def?.builderSlot || inferBuilderSlotFromKey(key),
      };
    })
    .filter((entry) => entry && (entry.points.length > 0 || entry.title));
};

const htmlToPlainText = (value = '') =>
  String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

export const mapSectionsToBuilderFields = (
  sectionEntries,
  promptConfig = null,
) => {
  const definitions = resolveSectionDefinitions(promptConfig);
  const defByKey = Object.fromEntries(definitions.map((d) => [d.key, d]));
  const mappedKeys = new Set();

  const result = {
    introduction: '',
    project_background: '',
    eligibility_points: [],
    financial_bid_details: '',
    overflowSections: [],
  };

  const appendText = (field, points) => {
    const text = points
      .map((p) => htmlToPlainText(p))
      .filter(Boolean)
      .join(' ');
    if (!text) return;
    result[field] = result[field] ? `${result[field]} ${text}` : text;
  };

  (sectionEntries || []).forEach((entry) => {
    const slot =
      entry.builderSlot ||
      defByKey[entry.key]?.builderSlot ||
      inferBuilderSlotFromKey(entry.key);

    mappedKeys.add(entry.key);

    if (slot === 'eligibility_points') {
      result.eligibility_points.push(...entry.points);
      return;
    }

    if (slot === 'introduction') {
      appendText('introduction', entry.points);
      return;
    }

    if (slot === 'project_background') {
      appendText('project_background', entry.points);
      return;
    }

    if (slot === 'financial_bid_details') {
      appendText('financial_bid_details', entry.points);
      return;
    }

    if (entry.points.length > 0) {
      result.overflowSections.push({
        key: entry.key,
        title: entry.title,
        points: entry.points,
      });
    }
  });

  return result;
};

export const buildTemplateVariableMap = (generatedContent, promptConfig) => {
  const entries = getSectionEntries(generatedContent, promptConfig);
  const mapped = mapSectionsToBuilderFields(entries, promptConfig);

  return {
    introduction: mapped.introduction,
    project_background: mapped.project_background,
    eligibility_points: mapped.eligibility_points,
    financial_bid_details: mapped.financial_bid_details,
    term_sheet: [
      { label: 'Bid Type', value: 'EOI' },
      { label: 'Evaluation Mode', value: 'Two Stage' },
    ],
    sectionMappings: entries.map((entry) => ({
      sectionKey: entry.key,
      sectionTitle: entry.title,
      builderField: entry.builderSlot || inferBuilderSlotFromKey(entry.key),
    })),
  };
};

export const sectionsToMarkdown = (sectionEntries, projectTitle) => {
  const lines = [`# ${projectTitle || 'REQUEST FOR PROPOSAL'}`, ''];

  (sectionEntries || []).forEach((section) => {
    if (!section?.title) return;
    lines.push('', `## ${section.title}`, '');
    (section.points || []).forEach((point) => {
      lines.push(`- ${point}`);
    });
  });

  lines.push('');
  return lines.join('\n');
};

export const buildLineItemsFromGeneratedContent = (
  generatedContent,
  promptConfig,
) => {
  const entries = getSectionEntries(generatedContent, promptConfig);
  const lines = [];

  entries.forEach((section) => {
    section.points.forEach((point) => {
      const text = String(point || '').trim();
      if (text) {
        lines.push({ description: text, requirement: section.title });
      }
    });
  });

  if (lines.length) return lines;

  return [
    {
      description: 'AI-generated scope item',
      requirement: 'Review and refine before vendor response collection',
    },
  ];
};

export const sectionsToHtml = (sectionEntries, projectTitle) => {
  const body = (sectionEntries || [])
    .map(
      (section) =>
        `<h2>${section.title}</h2>\n<ul>${(section.points || [])
          .map((p) => `<li>${p}</li>`)
          .join('')}</ul>`,
    )
    .join('\n');

  return body
    ? `<h1>${projectTitle || 'REQUEST FOR PROPOSAL'}</h1>\n${body}`
    : '';
};

/**
 * Snapshot stored on the RFP at creation time.
 * Existing RFPs always read from this — never from the live active prompt.
 */
export const buildRfpMetadataSnapshot = ({
  generatedContent,
  promptConfig,
  sectionOrder,
}) => {
  const sectionDefinitions = resolveSectionDefinitions(promptConfig);

  return {
    generatedContent: generatedContent || {},
    sectionDefinitions,
    sectionMapping: sectionDefinitions,
    sectionOrder:
      Array.isArray(sectionOrder) && sectionOrder.length
        ? sectionOrder
        : sectionDefinitions.map((d) => d.key),
    promptId: promptConfig?._id || null,
    promptName: promptConfig?.promptName || null,
    snapshotAt: new Date().toISOString(),
  };
};

const entryToExplorerSection = (entry, defByKey = {}) => ({
  key: entry.key,
  label: defByKey[entry.key]?.label || entry.title,
  content: entry.points.length
    ? entry.points.map((p) => `- ${p}`).join('\n')
    : '',
  points: entry.points,
});

/**
 * Build processing-page section cards from a saved RFP (metadata snapshot first).
 */
export const buildExplorerSectionsFromRfp = (rfp) => {
  const metadata = rfp?.metadata || {};
  const generatedContent = metadata.generatedContent || {};
  const promptConfig = {
    sectionMapping:
      metadata.sectionDefinitions?.length
        ? metadata.sectionDefinitions
        : metadata.sectionMapping,
  };

  const hasStructuredContent =
    generatedContent &&
    typeof generatedContent === 'object' &&
    Object.keys(generatedContent).some(
      (k) => generatedContent[k] && typeof generatedContent[k] === 'object',
    );

  if (hasStructuredContent) {
    const definitions = resolveSectionDefinitions(promptConfig);
    const defByKey = Object.fromEntries(definitions.map((d) => [d.key, d]));
    const sectionOrder = metadata.sectionOrder?.length
      ? metadata.sectionOrder
      : resolveSectionOrder(generatedContent, promptConfig);
    const entries = getSectionEntries(generatedContent, promptConfig);

    if (entries.length) {
      return entries
        .sort(
          (a, b) =>
            sectionOrder.indexOf(a.key) - sectionOrder.indexOf(b.key),
        )
        .map((entry) => entryToExplorerSection(entry, defByKey));
    }
  }

  const scopeText = String(rfp?.scope || '').trim();
  if (scopeText) {
    const chunks = scopeText
      .split(/(?=^## )/m)
      .map((chunk) => chunk.trim())
      .filter(Boolean);

    const fromScope = chunks
      .map((chunk, index) => {
        const headingMatch = chunk.match(/^##\s+(.+)/);
        const heading = headingMatch ? headingMatch[1].trim() : `Section ${index + 1}`;
        const body = chunk.replace(/^##\s+.+/m, '').trim();
        const points = body
          .split('\n')
          .map((l) => l.replace(/^[-*]\s+/, '').trim())
          .filter(Boolean);
        const slug = heading
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_|_$/g, '');

        return {
          key: slug || `section_${index + 1}`,
          label: heading,
          content: points.length ? points.map((p) => `- ${p}`).join('\n') : body,
          points,
        };
      })
      .filter((section) => section.content || section.points.length);

    if (fromScope.length) return fromScope;
  }

  return [];
};
